import { Controller } from "@hotwired/stimulus"
import ePub from "epubjs"
import { GlobalWorkerOptions } from "pdfjs-dist/legacy/build/pdf.mjs"
import { loadPdfDocument } from "../pdf_document_loader.js"
import ReadingMigrationController from "./reading_migration_controller.js"
import PdfReadingMigrationController from "./pdf_reading_migration_controller.js"

// One form can contain annotations targeting several independent editions.
export default class extends Controller {
  static targets = ["candidate", "submit", "status"]
  static values = { labels: Object, workerUrl: String }

  async connect() {
    this.ready = false
    this.submitTarget.disabled = true
    this.resolvers = new Map()
    this.abortController = new AbortController()
    const run = { abortController: this.abortController, resolvers: this.resolvers }
    try {
      // Sequential resolution avoids loading the same edition more than once.
      for (const candidate of this.candidateTargets) {
        const resolver = await this.resolverFor(candidate, run)
        if (run.abortController.signal.aborted) return
        try {
          const result = await resolver.resolveSemantic(candidate)
          if (run.abortController.signal.aborted) return
          this.locationField(candidate).value = JSON.stringify(result.location)
        } catch (error) {
          if (run.abortController.signal.aborted) return
          if (error.code !== "UNMAPPABLE_LOCATION") throw error
          candidate.disabled = true
          candidate.checked = false
          candidate.required = false
          this.locationField(candidate).value = ""
        }
      }
      if (run.abortController.signal.aborted) return
      this.markDiscardedItems()
      this.ready = true
      this.statusTarget.textContent = this.labelsValue.ready
      this.submitTarget.disabled = false
    } catch (error) {
      if (run.abortController.signal.aborted) return
      console.error("Unable to prepare user merge annotations", error)
      this.statusTarget.textContent = this.labelsValue.error
    }
  }

  locationField(candidate) {
    const key = `${candidate.dataset.fieldPrefix}:${candidate.value}`
    return this.element.querySelector(`[data-migration-location='${CSS.escape(key)}']`)
  }

  markDiscardedItems() {
    // Safari 17.2 and 17.3 are supported but do not implement Map.groupBy.
    const groups = new Map()
    for (const candidate of this.candidateTargets) {
      const key = candidate.dataset.fieldPrefix
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push(candidate)
    }
    for (const [key, candidates] of groups) {
      if (candidates.some((candidate) => !candidate.disabled)) continue
      const item = this.element.querySelector(`[data-merge-item='${CSS.escape(key)}']`)
      item.querySelector("[data-merge-discard]").disabled = false
      item.querySelector("[data-merge-deletion-warning]").hidden = false
      for (const candidate of candidates) this.locationField(candidate).disabled = true
    }
  }

  preventUnreadySubmit(event) {
    if (!this.ready) event.preventDefault()
  }

  async resolverFor(candidate, run) {
    const url = candidate.dataset.documentUrl
    if (run.resolvers.has(url)) return run.resolvers.get(url)

    let resolver
    if (candidate.dataset.format === "pdf") {
      GlobalWorkerOptions.workerSrc = this.workerUrlValue
      resolver = Object.create(PdfReadingMigrationController.prototype)
      run.resolvers.set(url, resolver)
      resolver.loadingTask = await loadPdfDocument({
        url, signal: run.abortController.signal,
        options: { enableScripting: false, isEvalSupported: false }
      })
      resolver.pdfDocument = await resolver.loadingTask.promise
    } else {
      resolver = Object.create(ReadingMigrationController.prototype)
      run.resolvers.set(url, resolver)
      resolver.book = ePub(url, { openAs: "opf", replacements: "none" })
      await resolver.book.ready
    }
    return resolver
  }

  disconnect() {
    this.ready = false
    this.abortController?.abort()
    for (const resolver of this.resolvers?.values() || []) {
      resolver.book?.destroy()
      resolver.loadingTask?.destroy()
    }
  }
}
